'use client'

import * as React from 'react'
import * as SegmentedGroupPrimitive from '@radix-ui/react-radio-group'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from '../../lib/utils'
import gsap from 'gsap'
import { useGSAP } from '@gsap/react'

const segmentedItemVariants = cva(
  'inline-flex items-center justify-center data-[state=checked]:rounded-xs font-regular data-[state=checked]:font-bold outline-none transition-[color] text-neutral-surface-60 data-[state=checked]:text-neutral-surface-100 whitespace-nowrap py-2xs px-xs cursor-pointer min-w-[5rem] data-[state=checked]:border data-[state=checked]:border-neutral-surface-5 data-[state=checked]:drop-shadow-xs data-[state=checked]:drop-shadow-black-20 z-10',
  {
    variants: {
      size: {
        '2xs': 'text-2xs',
        xs: 'text-xs',
        sm: 'text-sm',
        md: 'text-md',
        lg: 'text-lg',
      },
    },
    defaultVariants: {
      size: 'md',
    },
  },
)

const segmentedIndicatorBgStyles: Record<SegmentedGroupColor, string> = {
  neutral: 'bg-neutral-surface-light',
  base: 'bg-base-surface-light',
  // Add more mappings here as new colors are supported
}

const SegmentedGroupContext = React.createContext<
  VariantProps<typeof segmentedItemVariants> & { color?: SegmentedGroupColor }
>({
  size: 'md',
  color: 'neutral',
})

// Add type for color
export type SegmentedGroupColor = 'neutral' | 'base'

const segmentedGroupBgStyles: Record<SegmentedGroupColor, string> = {
  neutral: 'bg-neutral-surface-5',
  base: 'bg-base-surface-5',
  // Add more mappings here as new colors are supported
}

function SegmentedGroup({
  className,
  size,
  color = 'neutral',
  children,
  noIndicatorBg,
  ...props
}: React.ComponentProps<typeof SegmentedGroupPrimitive.Root> &
  VariantProps<typeof segmentedItemVariants> & {
    color?: SegmentedGroupColor
    noIndicatorBg?: boolean
  }) {
  const bgClass = segmentedGroupBgStyles[color] || 'bg-neutral-surface-5'
  const indicatorBg =
    segmentedIndicatorBgStyles[color] || 'bg-neutral-surface-light'
  const groupRef = React.useRef<HTMLDivElement>(null)
  const indicatorRef = React.useRef<HTMLDivElement>(null)
  const [activeItem, setActiveItem] = React.useState<HTMLElement | null>(null)
  const isLoaded = React.useRef(false)

  // Detect active item changes using MutationObserver
  React.useEffect(() => {
    if (!groupRef.current) return
    const updateActive = () => {
      const items = Array.from(
        groupRef.current!.querySelectorAll('[data-slot="radio-group-item"]'),
      )
      const active = items.find(
        (el) => el.getAttribute('data-state') === 'checked',
      ) as HTMLElement | undefined

      setActiveItem(active || null)
    }
    updateActive()
    const observer = new MutationObserver(updateActive)
    observer.observe(groupRef.current, {
      subtree: true,
      attributes: true,
      attributeFilter: ['data-state'],
    })
    setTimeout(() => {
      isLoaded.current = true
    }, 100)
    return () => observer.disconnect()
  }, [])

  // Animate indicator
  useGSAP(() => {
    if (
      noIndicatorBg ||
      !indicatorRef.current ||
      !activeItem ||
      !groupRef.current
    )
      return

    // Kill any ongoing animations to prevent conflicts
    gsap.killTweensOf(indicatorRef.current)

    requestAnimationFrame(() => {
      if (
        !indicatorRef.current ||
        !activeItem ||
        !groupRef.current ||
        noIndicatorBg
      )
        return

      const tabRect = activeItem.getBoundingClientRect()
      const containerRect = groupRef.current.getBoundingClientRect()

      const x = tabRect.right - containerRect.right
      const y = tabRect.top - containerRect.top
      const width = tabRect.width
      const height = tabRect.height

      gsap.to(indicatorRef.current, {
        x,
        width,
        y,
        height,
        duration: isLoaded.current ? 0.35 : 0,
        ease: 'power2.out',
        overwrite: true,
      })
    })
  }, [activeItem, noIndicatorBg, isLoaded.current])

  return (
    <SegmentedGroupPrimitive.Root
      ref={groupRef}
      data-slot='toggle-group'
      data-size={size}
      className={cn(
        'group/toggle-group  z-1 flex w-fit items-center rounded-xs  relative overflow-hidden',
        bgClass,
        !noIndicatorBg ? 'p-2xs' : '',
        className,
      )}
      {...props}
    >
      {/* Animated indicator */}
      {!noIndicatorBg && (
        <div
          ref={indicatorRef}
          className={cn(
            'absolute right-0 top-0 rounded-xs transition-none pointer-events-none z-0',
            indicatorBg,
          )}
          style={{ willChange: 'transform, width, height' }}
        />
      )}
      <SegmentedGroupContext.Provider value={{ size, color }}>
        {children}
      </SegmentedGroupContext.Provider>
    </SegmentedGroupPrimitive.Root>
  )
}

function SegmentedGroupItem({
  className,
  children,
  size,
  ...props
}: React.ComponentProps<typeof SegmentedGroupPrimitive.Item> &
  VariantProps<typeof segmentedItemVariants>) {
  const context = React.useContext(SegmentedGroupContext)
  return (
    <SegmentedGroupPrimitive.Item
      data-slot='radio-group-item'
      data-size={context.size || size}
      className={cn(
        segmentedItemVariants({
          size: context.size || size,
        }),
        className,
      )}
      {...props}
    >
      {children}
    </SegmentedGroupPrimitive.Item>
  )
}

export { SegmentedGroup, SegmentedGroupItem }
